import SimpleHeader from "../../components/SimpleHeader/SimpleHeader";
import {
	Container,
	Row,
	Col,
	Card,
	CardHeader,
	CardBody,
	Input,
} from "reactstrap";
import React, { useState, useEffect } from "react";
import SearchInput from "../../components/Search/SearchInput";
import { SquareCheckbox } from "../../components/Inputs/SquareCheckbox/SquareCheckbox";
import UserManagementTable from "./UserManagementTable";
import CreateUpdateUserModal from "./Modal/CreateUpdateUserModal";
import { useGetTenantUsersQuery } from "../../utilities/queries/useGetTenantUsersQuery";
import { useStore, toastToggleCallback } from "../../utilities/appStore";
import { ToastMessage } from "../../components/ToastMessage/ToastMessage";
import { makeToast } from '../../utilities/appStore';
import BulkDeleteUsersModal from "./Modal/BulkDeleteUsersModal";
import { bulkDeleteUsers } from "../../utilities/orgPanelApiCalls";
import { errorToast, successToast } from "../../constants/toast";
import Button from "../../components/UIButton/UIButton";
import BootstrapSelect from "../../components/BootstrapSelect/BootstrapSelect";
import Spinner from "../../components/UISpinner/UISpinner";
import { UserAccountType } from '../../constants/UserAccountType';

const UserManagementPage = () => {
	const isGlobalLoading = useStore((state) => state.isLoading);
	const orgId = useStore((state) => state.CurrentOrganizationId);
	const toast = useStore((state) => state.toast);
	const { data, isLoading, error, refetch } = useGetTenantUsersQuery(orgId);

	const [search, setSearch] = useState("");
	const [filteredData, setFilteredData] = useState([]);
	const [selectedAccounts, setSelectedAccounts] = useState([]);
	const [filterBy, setFilterBy] = useState();
	const [isCreateModalOpen, setCreateModalOpen] = useState(false);
	const [modalEditId, setModalEditId] = useState(null); // null = create
	const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
	const [bulkDeleteAccounts, setBulkDeleteAccounts] = useState([]);

	const filterOptions = [
		{ value: "All", label: "All" },
		{ value: "Pending", label: "Pending" },
		{ value: "Assigned", label: "Assigned" },
		{ value: "Shared Users", label: "Shared Users" },
		{ value: "Single Users", label: "Single Users" },
	];

	const editUserClickHandler = (accountId) => {
		setModalEditId(accountId);
		setCreateModalOpen(true);
	};

	const toggleCreateModal = () => {
		setCreateModalOpen((prev) => !prev);
		setTimeout(() => {
			setModalEditId(null); // reset modalType or form after modal is unmounted
		}, 200);
	};
	const handleCreateNewUserClick = () => {
		setCreateModalOpen(true);
	};

	const doesEmailExistInUserData = (modelData) => {
		
		const errors = {};

		if (modelData.accountType === UserAccountType.Shared) {
			const doesExist = data?.some(
				(item) => item.name && item.name.trim().toLowerCase() === modelData.userData?.userName
			);

			if (doesExist) {
				errors.userName = "User with the same username exists";
			}

		}
		else {
			const doesExist = data?.some(
				(item) => item.email && item.email.trim().toLowerCase() === modelData.userData?.email.trim().toLowerCase()
			);

			if (doesExist) {
				errors.email = "User with the same email exists";
			}
		}

		return errors;
	};

	const showBulkUserDeleteModalForSingleUserRemoval = (accountId) => {
		if (!accountId) {
			return;
		}
		setSelectedAccounts([accountId]);
		setBulkDeleteAccounts(data.filter(u => u.accountId === accountId));
		setShowBulkDeleteModal(true);
	}

	function handleShowBulkDeleteModal() {
		let accountList = data
			.filter((x) => selectedAccounts.includes(x.accountId));

		setBulkDeleteAccounts(accountList);
		setShowBulkDeleteModal(true);	
	}

	async function handleDeleteSelectedUsers() {
		try{
			var result = await bulkDeleteUsers(orgId, selectedAccounts);

			if (result){
				makeToast("Users deleted successfully.", successToast);
			}
			else {
				makeToast("No users were found for the selected company. Please try again.", errorToast);
			}

			setShowBulkDeleteModal(false);	
			refetch(); //refresh user list
			setSelectedAccounts([]);
		}
		catch (error) {
			makeToast("An error occurred while deleting users.", errorToast);
		}
	}

	useEffect(() => {
		const test = !error;
		if (data && !error) {
			let filtered = data;

			if (search) {
				filtered = filtered.filter((x) =>
					x.name.toLowerCase().includes(search.toLowerCase())
				);
			}

			if (filterBy === "Pending") {
				filtered = filtered.filter((x) => x.userStatus === 1);
			}

			if (filterBy === "Assigned") {
				filtered = filtered.filter((x) => x.userStatus === 2);
			}

			if (filterBy === "Shared Users") {
				filtered = filtered.filter((x) => x.userType === 2);
			}

			if (filterBy === "Single Users") {
				filtered = filtered.filter((x) => x.userType === 1);
			}

			setFilteredData(filtered);
		}
	}, [search, data, filterBy, error]);

	return (
		<>
			{toast?.display && (
				<ToastMessage
					Message={toast?.message}
					Type={toast?.type}
					ToggleCallback={toastToggleCallback}
				/>
			)}
			{showBulkDeleteModal && <BulkDeleteUsersModal
				visible={showBulkDeleteModal}
				toggle={() => setShowBulkDeleteModal(!showBulkDeleteModal)}
				userAccountIds={bulkDeleteAccounts}
				handleSubmit={handleDeleteSelectedUsers}
				size="md"
			/>}
			<CreateUpdateUserModal
				isOpen={isCreateModalOpen}
				toggle={toggleCreateModal}
				modalEditId={modalEditId}
				doesEmailExistInUserData={doesEmailExistInUserData}
				openRemoveUserModal={showBulkUserDeleteModalForSingleUserRemoval}
			/>
			<SimpleHeader parentName="User Management" />
			{isGlobalLoading && <Spinner />}
			{!isGlobalLoading && (
				<Container className="mt--6 overflow-hidden" fluid>
					<Row className="justify-content-center">
						<Col className="card-wrapper">
							<Card className="card-header-devices">
								<CardHeader>
									<Container fluid>
										<Row>
											<Col>
												<h2>User Management</h2>
											</Col>
										</Row>
										<Row>
											<Col sm={8}>
												<Row className="align-items-center my-2">
													<Col lg={6} className="my-1">
														<SearchInput
															value={search}
															onChange={(e) => setSearch(e.target.value)}
														/>
													</Col>

													<Col lg={5} className="my-1">
														<Row className="align-items-center text-center">
															<div className="font-size-12px">
																Filter by
															</div>
															<Col>
																<BootstrapSelect
																	size="sm"
																	name="state"
																	options={filterOptions}
																	value={filterOptions.find(opt => opt.value === filterBy) || filterOptions[0]} // defaults to "All"
																	onChange={(opt) => setFilterBy(opt.value)}
																	required
																	width="150px"
																/>
															</Col>
														</Row>
													</Col>
												</Row>
											</Col>
											<Col sm={4} className="justify-content-end d-flex">
												<Row className="my-2 align-items-center">
													<Col lg={6} className="my-1">
														<Button
															variant="danger"
															size="medium"
															disabled={selectedAccounts.length === 0}
															onClick={handleShowBulkDeleteModal}
														>
															Remove Users
														</Button>
													</Col>
													<Col lg={6} className="my-1">
														<Button
															variant="primary"
															size="medium"
															onClick={handleCreateNewUserClick}
														>
															+ Add New User
														</Button>
													</Col>
												</Row>
											</Col>
										</Row>
									</Container>
								</CardHeader>
								<CardBody>
									<UserManagementTable
										userData={filteredData}
										isLoading={isLoading}
										editClickHandler={editUserClickHandler}
										selectedAccounts={selectedAccounts}
										setSelectedAccounts={setSelectedAccounts}
									/>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			)}
		</>
	);
};

export default UserManagementPage;
